import type { GetStaticPaths, GetStaticProps } from "next";
import type { ReactNode } from "react";
import type { Listing } from "@/lib/construction/types";
import type { CompanyContent } from "@/lib/construction/content";

import Link from "next/link";
import { ArrowUpRight, Globe, MapPin } from "lucide-react";

import { Meta } from "@/components/seo/Meta";
import { Header } from "@/components/sections/Header";
import { Footer } from "@/components/sections/Footer";
import { FaqBlock } from "@/components/sections/content/FaqBlock";
import { Container } from "@/components/ui/Container";
import { PlaceHero } from "@/components/construction/PlaceHero";
import { CompanyRow } from "@/components/construction/CompanyRow";
import { ServiceLinks } from "@/components/construction/ServiceLinks";
import { CompaniesHouseAttribution, OsmAttribution, PostcodeAttribution } from "@/components/construction/Attribution";
import { buildCompanyContent, fullAddress } from "@/lib/construction/content";
import { companyMeta } from "@/lib/construction/meta";
import { companyGraph, graphHtml } from "@/lib/construction/schema";
import { DIRECTORY_PATH, companyHref, getRegion, isRegionSlug, regionHref, townHref, townKey } from "@/lib/construction/places";
import { isIndexableListing, websiteHost } from "@/lib/construction/normalise";
import { getTrade, primaryTrade } from "@/lib/construction/trades";
import { DIRECTORY_REVALIDATE } from "@/lib/construction/runtime";

type Nearby = Listing & { distanceKm: number };

type CompanyPageProps = {
  listing: Listing;
  content: CompanyContent;
  nearest: Nearby[];
  others: Listing[];
  meta: { title: string; description: string };
};

function osmUrl(sourceKey: string): string | null {
  const match = /^osm:(node|way|relation)\/(\d+)$/.exec(sourceKey);
  return match ? `https://www.openstreetmap.org/${match[1]}/${match[2]}` : null;
}

function H2({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2 id={id} className="font-display text-display-md font-bold tracking-tight text-ink text-balance">
      {children}
    </h2>
  );
}

export default function CompanyPage({ listing: l, content, nearest, others, meta }: CompanyPageProps) {
  const region = getRegion(l.region);
  const href = companyHref(l.region, l.townSlug, l.slug);
  // Inline CMS editing: every database value is wrapped in its own element.
  const cms = (column: string) => `companies:${l.rowId}:${column}`;
  const Name = () => <span data-mdlfy-cms={cms("name")}>{l.name}</span>;
  const Town = () => <span data-mdlfy-cms={cms("town")}>{l.town}</span>;
  const Address = () => (
    <>
      {l.address ? <span data-mdlfy-cms={cms("address")}>{l.address}</span> : null}
      {l.address && l.postcode ? ", " : null}
      {l.postcode ? <span data-mdlfy-cms={cms("postcode")}>{l.postcode}</span> : null}
    </>
  );
  const OtherName = ({ listing }: { listing: Listing }) => <span data-mdlfy-cms={`companies:${listing.rowId}:name`}>{listing.name}</span>;
  const OtherTown = ({ listing }: { listing: Listing }) => <span data-mdlfy-cms={`companies:${listing.rowId}:town`}>{listing.town}</span>;

  const crumbs = [
    { name: "Home", href: "/" },
    { name: "Construction companies", href: DIRECTORY_PATH },
    { name: region?.name ?? "Region", href: regionHref(l.region) },
    { name: l.town, href: townHref(l.region, l.townSlug), cms: cms("town") },
    { name: l.name, href, cms: cms("name") },
  ];
  const trade = primaryTrade(l.trades);
  const address = fullAddress(l);
  const host = websiteHost(l.website);
  const osm = osmUrl(l.sourceKey);
  const register = l.source === "ch";
  const ch = content.companiesHouse;
  const hasPoint = l.lat !== null && l.lng !== null;
  const mapQuery = register && l.postcode ? l.postcode : hasPoint ? `${l.lat},${l.lng}` : l.postcode;
  const viewMapHref = mapQuery ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}` : null;

  const graph = companyGraph({ listing: l, name: l.name, description: meta.description, crumbs, faqs: content.faqs });

  return (
    <>
      <Meta
        absoluteTitle={meta.title}
        description={meta.description}
        canonical={href}
        noindex={!isIndexableListing(l)}
        follow
        ogImageAlt={`${l.name}, ${l.town}`}
      />
      <Header />
      <main id="main">
        <PlaceHero
          crumbs={crumbs}
          eyebrow={
            <>
              {trade ? trade.label : "Construction company"} · <Town />
            </>
          }
          title={<Name />}
          answer={content.lede}
        />

        <Container className="py-14 md:py-20">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="space-y-14 lg:col-span-7">
              <section aria-labelledby="about">
                <H2 id="about">
                  What is <Name />?
                </H2>
                <div className="prose-content mt-5">
                  {content.about.map((p) => (
                    <p key={p}>{p}</p>
                  ))}
                </div>
              </section>

              <section aria-labelledby="trades">
                <H2 id="trades">
                  Which trades is <Name /> listed under?
                </H2>
                <div className="prose-content mt-5">
                  <p>{content.tradesSentence}</p>
                </div>
                <ul className="mt-5 flex flex-wrap gap-2">
                  {l.trades.map((slug) => {
                    const t = getTrade(slug);
                    return t ? (
                      <li key={slug}>
                        <Link
                          href={`/industries/${t.industry}`}
                          className="inline-flex rounded-full border border-ink/10 bg-white px-3.5 py-1.5 text-sm font-medium text-ink transition-colors hover:border-ink/30"
                        >
                          {t.label}: website design for {t.plural}
                        </Link>
                      </li>
                    ) : null;
                  })}
                </ul>
              </section>

              <section aria-labelledby="location">
                <H2 id="location">
                  {register ? (
                    <>
                      Where is <Name />
                      &apos;s registered office?
                    </>
                  ) : (
                    <>
                      Where is <Name /> located?
                    </>
                  )}
                </H2>
                <div className="prose-content mt-5">
                  {register ? (
                    <p>
                      <Name /> has its registered office in <Town />, {region?.longName}.{" "}
                      {address ? (
                        <>
                          Companies House gives the registered office address as <Address />.{" "}
                        </>
                      ) : null}
                      A registered office is the official address on the Companies House register, which is not always where a company carries out
                      its work.
                    </p>
                  ) : (
                    <p>
                      <Name /> is in <Town />, {region?.longName}.{" "}
                      {address ? (
                        <>
                          The address recorded in OpenStreetMap is <Address />.
                        </>
                      ) : (
                        "OpenStreetMap does not record a street address, so the map shows its mapped location."
                      )}
                    </p>
                  )}
                </div>
              </section>

              {l.phone || l.website ? (
                <section aria-labelledby="contact">
                  <H2 id="contact">
                    How can you contact <Name />?
                  </H2>
                  <div className="prose-content mt-5">
                    <p>
                      You can contact <Name />
                      {l.phone ? (
                        <>
                          {" "}by phone on{" "}
                          <a href={`tel:${l.phone.replace(/[^\d+]/g, "")}`}>
                            <span data-mdlfy-cms={cms("phone")}>{l.phone}</span>
                          </a>
                        </>
                      ) : null}
                      {l.phone && l.website && host ? " or" : null}
                      {l.website && host ? (
                        <>
                          {" "}through its website,{" "}
                          <a href={l.website} target="_blank" rel="nofollow noopener noreferrer">
                            {host}
                          </a>
                        </>
                      ) : null}
                      .{" "}
                      {l.websiteSource === "register-number" && !l.phone ? (
                        <>
                          ConstructionWebDesign.uk matched this website because it shows <Name />
                          &apos;s company number{ch ? `, ${ch.number}` : ""}, which UK companies must display on their websites.
                        </>
                      ) : (
                        "These details come from OpenStreetMap, so check them before relying on them."
                      )}
                    </p>
                  </div>
                </section>
              ) : null}

              {ch ? (
                <section aria-labelledby="companies-house">
                  <H2 id="companies-house">
                    Is <Name /> on Companies House?
                  </H2>
                  <div className="prose-content mt-5">
                    <p>
                      Yes. Companies House lists company number {ch.number} for <Name />
                      {ch.status ? `, with the status "${ch.status}"` : ""}
                      {ch.incorporated ? `, incorporated on ${ch.incorporated}` : ""}.
                      {ch.sicLabels.length ? ` Its ${ch.sicLabels.length === 1 ? "SIC code is" : "SIC codes are"} ${ch.sicLabels.join(", ")}.` : ""}
                      {ch.registeredName ? (
                        <>
                          {" "}Its name on the register is <span data-mdlfy-cms={cms("ch_name")}>{ch.registeredName}</span>.
                        </>
                      ) : null}
                    </p>
                    <p className="text-sm">
                      Source:{" "}
                      <a
                        href={`https://find-and-update.company-information.service.gov.uk/company/${encodeURIComponent(ch.number)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Companies House
                      </a>
                    </p>
                  </div>
                </section>
              ) : null}

              {content.ranking ? (
                <section aria-labelledby="size">
                  <H2 id="size">
                    How big is{" "}
                    {l.ranking ? <span data-mdlfy-cms={`company_rankings:${l.ranking.id}:name`}>{l.ranking.name}</span> : <Name />}?
                  </H2>
                  <div className="prose-content mt-5">
                    <p>{content.ranking.sentence}</p>
                    {content.ranking.sourceUrl ? (
                      <p className="text-sm">
                        Source:{" "}
                        <a href={content.ranking.sourceUrl} target="_blank" rel="noopener noreferrer">
                          {content.ranking.sourceName ?? "company report"}
                        </a>
                      </p>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {nearest.length ? (
                <section aria-labelledby="nearest">
                  <H2 id="nearest">
                    Which construction companies are near <Name />?
                  </H2>
                  <p className="mt-4 text-ink-muted">
                    The closest construction company to <Name /> in the directory is <OtherName listing={nearest[0]} /> in{" "}
                    <OtherTown listing={nearest[0]} />, about {nearest[0].distanceKm < 1 ? "under 1" : Math.round(nearest[0].distanceKm)} km away
                    {register ? " (measured between registered office postcodes and mapped locations)" : ""}.
                  </p>
                  <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                    {nearest.map((n) => (
                      <CompanyRow key={n.key} listing={n} showRegion={false} />
                    ))}
                  </ul>
                </section>
              ) : null}

              {others.length ? (
                <section aria-labelledby="others">
                  <H2 id="others">
                    What other construction companies are in <Town />?
                  </H2>
                  <p className="mt-4 text-ink-muted">
                    Other construction companies listed in <Town /> include <OtherName listing={others[0]} />
                    {others[1] ? (
                      <>
                        {" "}and <OtherName listing={others[1]} />
                      </>
                    ) : null}
                    .{" "}
                    <Link href={townHref(l.region, l.townSlug)} className="font-medium text-ink underline decoration-accent decoration-2 underline-offset-4">
                      See every construction company in <Town />
                    </Link>
                    .
                  </p>
                  <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                    {others.map((o) => (
                      <CompanyRow key={o.key} listing={o} showRegion={false} />
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>

            <aside aria-label={`${l.name} details`} className="relative z-10 lg:col-span-5 lg:-mt-36">
              <div className="space-y-5 lg:sticky lg:top-28">
                <div className="rounded-2xl border border-ink/[0.08] bg-white p-6 shadow-[0_20px_50px_-20px_rgb(0_0_0/0.25)]">
                  <dl className="space-y-4 text-sm">
                    {address ? (
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">{register ? "Registered office" : "Address"}</dt>
                        <dd className="mt-1 text-ink">
                          <Address />
                        </dd>
                      </div>
                    ) : null}
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">Town</dt>
                      <dd className="mt-1">
                        <Link href={townHref(l.region, l.townSlug)} className="font-medium text-ink underline underline-offset-4">
                          <Town />
                        </Link>
                        , {region?.name}
                      </dd>
                    </div>
                    {l.phone ? (
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">Phone</dt>
                        <dd className="mt-1">
                          <a href={`tel:${l.phone.replace(/[^\d+]/g, "")}`} className="font-medium text-ink underline underline-offset-4">
                            <span data-mdlfy-cms={cms("phone")}>{l.phone}</span>
                          </a>
                        </dd>
                      </div>
                    ) : null}
                    {l.website && host ? (
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">Website</dt>
                        <dd className="mt-1">
                          <a
                            href={l.website}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="mt-1 inline-flex max-w-full items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                          >
                            <Globe className="size-4 shrink-0" aria-hidden="true" />
                            <span className="truncate">{host}</span>
                            <ArrowUpRight className="size-4 shrink-0" aria-hidden="true" />
                            <span className="sr-only">(opens in a new tab)</span>
                          </a>
                        </dd>
                      </div>
                    ) : null}
                    {ch ? (
                      <>
                        <div>
                          <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">Company number</dt>
                          <dd className="mt-1 text-ink">
                            {ch.number} <span className="text-ink-muted">(Source: Companies House)</span>
                          </dd>
                        </div>
                        {ch.companyType ? (
                          <div>
                            <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">Company type</dt>
                            <dd className="mt-1 text-ink">
                              <span data-mdlfy-cms={cms("ch_company_type")}>{ch.companyType}</span>
                            </dd>
                          </div>
                        ) : null}
                        {ch.incorporated ? (
                          <div>
                            <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">Incorporated</dt>
                            <dd className="mt-1 text-ink">{ch.incorporated}</dd>
                          </div>
                        ) : null}
                        {ch.status ? (
                          <div>
                            <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">Status</dt>
                            <dd className="mt-1 text-ink">{ch.status}</dd>
                          </div>
                        ) : null}
                        {ch.sicLabels.length ? (
                          <div>
                            <dt className="text-[11px] font-semibold uppercase tracking-caps text-ink-muted">SIC codes</dt>
                            <dd className="mt-1 text-ink">
                              <ul className="space-y-1">
                                {ch.sicLabels.map((s) => (
                                  <li key={s}>{s}</li>
                                ))}
                              </ul>
                            </dd>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </dl>
                </div>

                {viewMapHref ? (
                  <div className="flex flex-wrap gap-3">
                      <a
                        href={viewMapHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                      >
                        <MapPin className="size-4 shrink-0" aria-hidden="true" />
                        View on Google Maps
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                  </div>
                ) : null}

                {register ? (
                  <p className="text-xs text-ink-muted">
                    Something wrong? Register details are updated at Companies House and refresh here monthly. Email{" "}
                    <a href="mailto:hello@constructionwebdesign.uk" className="underline underline-offset-2">
                      hello@constructionwebdesign.uk
                    </a>{" "}
                    to remove this listing.
                  </p>
                ) : (
                  <p className="text-xs text-ink-muted">
                    Something wrong? Correct the listing on OpenStreetMap, or email{" "}
                    <a href="mailto:hello@constructionwebdesign.uk" className="underline underline-offset-2">
                      hello@constructionwebdesign.uk
                    </a>{" "}
                    to remove it.
                  </p>
                )}
                {register ? (
                  <>
                    <CompaniesHouseAttribution />
                    {hasPoint ? <PostcodeAttribution /> : null}
                  </>
                ) : (
                  <>
                    <OsmAttribution />
                    {ch ? <CompaniesHouseAttribution /> : null}
                  </>
                )}
              </div>
            </aside>
          </div>
        </Container>

        {content.faqs.length ? (
          <FaqBlock
            title={
              <>
                <Name />: questions
              </>
            }
            faqs={content.faqs}
          />
        ) : null}

        <ServiceLinks place={l.town} trades={l.trades} />
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: graphHtml(graph) }} />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: "blocking" });

export const getStaticProps: GetStaticProps<CompanyPageProps> = async ({ params }) => {
  const regionParam = typeof params?.region === "string" ? params.region : "";
  const townParam = typeof params?.town === "string" ? params.town : "";
  const companyParam = typeof params?.company === "string" ? params.company : "";
  if (!isRegionSlug(regionParam) || !townParam || !companyParam) return { notFound: true, revalidate: DIRECTORY_REVALIDATE };

  const { maybeStartImport } = await import("@/lib/construction/import");
  const data = await import("@/lib/construction/data");
  void maybeStartImport();

  const key = townKey(regionParam, townParam);
  const listing = await data.getListing(key, companyParam);
  if (!listing) return { notFound: true, revalidate: DIRECTORY_REVALIDATE };

  const [town, nearest, sameTown] = await Promise.all([
    data.getTown(key),
    listing.lat !== null && listing.lng !== null ? data.getNearbyListings(listing.lat, listing.lng, listing.key, 6) : Promise.resolve([] as Nearby[]),
    data.searchListings({ towns: [key], sort: "size", limit: 9 }),
  ]);
  const others = sameTown.items.filter((o) => o.key !== listing.key).slice(0, 8);

  const content = buildCompanyContent(listing, {
    townCount: town?.count ?? 1,
    nearest: nearest.map((n) => ({ name: n.name, town: n.town, distanceKm: n.distanceKm })),
    townLat: town?.lat ?? null,
    townLng: town?.lng ?? null,
  });

  return {
    props: { listing, content, nearest, others, meta: companyMeta(listing) },
    revalidate: DIRECTORY_REVALIDATE,
  };
};
